import { useTranslate } from '@ur-solutions/react-hooks'
import type { AbsenceDetailsNode } from '../absence-details'
import { AbsenceDetailsCard } from '../absence-details-card'
import { format } from '@/utils'
import { AbsenceStatusValue } from '../../consts'
import { ActionLogsList } from '../../components/action-logs-list'
import type { ActionFieldRecord } from './util'
import {
  quillDeltaToPlainText,
  tryBuildQuillDelta,
} from './util'
import { subSeconds } from 'date-fns'

interface Props {
  absence: AbsenceDetailsNode
}

export function AbsenceLogs({ absence }: Props) {
  const translations = useTranslate({
    activity: 'absence.details.log',
    noActivity: 'absence.details.log.no-activity',

    absenceType: 'timesheets.absence-type',
    reason: 'common.reason',
    start: 'common.start',
    end: 'common.end',
    status: 'common.status',
    comment: 'common.comment',
    comments: 'common.comments',
    file: 'common.file',
    files: 'common.files',
    unknown: 'common.unknown',

    approved: 'timesheets.absence-approved',
    pending: 'timesheets.absence-pending',
    rejected: 'timesheets.absence-rejected',

    logSummary: [
      'absence.details.log.summary',
      { user: '', action: '', target: '' },
    ],
    logActionAdded: 'absence.details.log.action-added',
    logActionCreated: 'absence.details.log.action-created',
    logActionUpdated: 'absence.details.log.action-updated',
    logActionDeleted: 'absence.details.log.action-deleted',
    logTargetComment: 'absence.details.log.target-comment',
    logTargetFile: 'absence.details.log.target-file',
    logTargetAbsence: 'absence.details.log.target-absence',
    logTargetStatus: 'absence.details.log.target-status',
    logShowDetails: 'absence.details.log.show-details',
    logHideDetails: 'absence.details.log.hide-details',
    logTabNew: 'absence.details.log.tab-new',
    logTabOld: 'absence.details.log.tab-old',
  })

  const logs = [...absence.actionLogs].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )

  function getActionFieldLabel(field: string) {
    if (field === 'absence_type_id') return translations.absenceType
    if (field === 'reason') return translations.reason
    if (field === 'datetime_start') return translations.start
    if (field === 'datetime_end') return translations.end
    if (field === 'request_status') return translations.status
    if (field === 'comment') return translations.comments
    if (field === 'file') return translations.files
    return field.replaceAll('_', ' ')
  }

  function getActionFieldDetailLabel(field: string) {
    if (field === 'comment') return translations.comment
    if (field === 'file') return translations.file
    return getActionFieldLabel(field)
  }

  function getOrderedFieldEntries(fields: ActionFieldRecord) {
    const order: Record<string, number> = {
      comment: 10,
      file: 20,
      request_status: 30,
      absence_type_id: 40,
      reason: 50,
      datetime_start: 60,
      datetime_end: 61,
    }

    return Object.entries(fields).sort(([fieldA], [fieldB]) => {
      const rankA = order[fieldA] ?? 999
      const rankB = order[fieldB] ?? 999
      if (rankA !== rankB) return rankA - rankB
      return fieldA.localeCompare(fieldB)
    })
  }

  function normalizeAction(value: string) {
    return value.toUpperCase()
  }

  function getLogActionWord(action: string, targetField: string | null) {
    const normalizedAction = normalizeAction(action)

    if (normalizedAction === 'CREATED') {
      if (targetField === 'comment' || targetField === 'file') {
        return translations.logActionAdded
      }
      return translations.logActionCreated
    }
    if (normalizedAction === 'UPDATED') return translations.logActionUpdated
    if (normalizedAction === 'DELETED') return translations.logActionDeleted
    return action.toLowerCase()
  }

  function getStatusLabel(value: string) {
    const normalized = value.toUpperCase()
    if (normalized === AbsenceStatusValue.PENDING) return translations.pending
    if (normalized === AbsenceStatusValue.APPROVED) return translations.approved
    if (normalized === AbsenceStatusValue.REJECTED) return translations.rejected
    return value
  }

  function getLogSummary(
    log: (typeof logs)[number],
    fields: ActionFieldRecord
  ) {
    const orderedFieldEntries = getOrderedFieldEntries(fields)
    const orderedFieldKeys = orderedFieldEntries.map(([fieldName]) => fieldName)
    const primaryField = orderedFieldKeys[0] ?? null

    const actionWord = getLogActionWord(log.action, primaryField)
    const actorName = log.user?.fullName || translations.unknown

    if (primaryField === 'comment') {
      return translations.logSummary({
        user: actorName,
        action: actionWord,
        target: translations.logTargetComment,
      })
    }

    if (primaryField === 'file') {
      return translations.logSummary({
        user: actorName,
        action: actionWord,
        target: translations.logTargetFile,
      })
    }

    if (primaryField === 'request_status') {
      const statusValue = fields.request_status?.new_value
      const statusLabel =
        typeof statusValue === 'string'
          ? getStatusLabel(statusValue)
          : translations.unknown

      return translations.logSummary({
        user: actorName,
        action: translations.logActionUpdated,
        target: `${translations.logTargetStatus} ${statusLabel}`,
      })
    }

    if (orderedFieldKeys.length > 1) {
      const targets = orderedFieldKeys
        .map(fieldName => getActionFieldDetailLabel(fieldName).toLowerCase())
        .join(', ')

      return translations.logSummary({
        user: actorName,
        action: actionWord,
        target: targets,
      })
    }

    if (primaryField == null) {
      return translations.logSummary({
        user: actorName,
        action: translations.logActionUpdated,
        target: translations.logTargetAbsence,
      })
    }

    return translations.logSummary({
      user: actorName,
      action: actionWord,
      target: getActionFieldLabel(primaryField).toLowerCase(),
    })
  }

  function formatAbsenceDateFieldValue(side: 'start' | 'end', value: string) {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value

    const durationMode = absence.absenceType?.durationMode
    if (!durationMode || durationMode === 'HOURLY') {
      return format(date, 'dd.MM.yyyy HH:mm')
    }

    const normalizedDate = side === 'start' ? date : subSeconds(date, 1)
    return format(normalizedDate, 'dd.MM.yyyy')
  }

  function formatActionFieldValue(field: string, raw: unknown): string {
    if (raw == null || raw === '') return '-'

    const quillDelta = tryBuildQuillDelta(raw)
    if (!!quillDelta) return quillDeltaToPlainText(quillDelta)

    if (
      (field === 'datetime_start' || field === 'datetime_end') &&
      typeof raw === 'string'
    )
      return formatAbsenceDateFieldValue(
        field === 'datetime_start' ? 'start' : 'end',
        raw
      )

    if (field === 'request_status' && typeof raw === 'string') {
      return getStatusLabel(raw)
    }

    if (field === 'absence_type_id') {
      if (String(raw) === absence.absenceType?.id) {
        return absence.absenceType.name
      }
      return `#${raw}`
    }

    if (Array.isArray(raw)) {
      if (!raw.length) return '-'
      return raw.map(entry => formatActionFieldValue(field, entry)).join(', ')
    }

    if (raw && typeof raw === 'object') {
      const objectValue = raw as Record<string, unknown>

      if (typeof objectValue.file_name === 'string' && objectValue.file_name) {
        return objectValue.file_name
      }

      if (typeof objectValue.name === 'string' && objectValue.name) {
        return objectValue.name
      }

      if (typeof objectValue.content === 'string' && objectValue.content) {
        return objectValue.content
      }

      const formattedObject: string = Object.entries(objectValue)
        .filter(([key]) => !['id'].includes(key))
        .map(([key, value]) => {
          const formattedValue: string = formatActionFieldValue(field, value)
          return `${key.replaceAll('_', ' ')}: ${formattedValue}`
        })
        .join(', ')

      return formattedObject || '-'
    }

    return String(raw)
  }

  return (
    <AbsenceDetailsCard
      type="logs"
      title={translations.activity}
      icon="clock-rotate-left"
    >
      <ActionLogsList
        logs={logs}
        noActivityText={translations.noActivity}
        showDetailsText={translations.logShowDetails}
        hideDetailsText={translations.logHideDetails}
        oldTabLabel={translations.logTabOld}
        newTabLabel={translations.logTabNew}
        getLogSummary={getLogSummary}
        getOrderedFieldEntries={getOrderedFieldEntries}
        getFieldLabel={getActionFieldDetailLabel}
        formatFieldValue={formatActionFieldValue}
        shouldUseOldNewTabs={({ fieldName, value }) =>
          fieldName === 'comment' ||
          fieldName === 'reason' ||
          !!tryBuildQuillDelta(value.old_value) ||
          !!tryBuildQuillDelta(value.new_value)
        }
      />
    </AbsenceDetailsCard>
  )
}
